import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "../mock/mock";
import { Button } from "./ui/button";
import { SiteLink } from "./SiteLink";
import { mainHref } from "../lib/siteUrls";

// Official Sky's the Limit wordmark (inlined from skysthelimit.org nav SVG).
// fill=currentColor so it adapts: white on the dark navbar, black when scrolled.
function Logo({ dark }) {
  return (
    <svg
      viewBox="0 0 84 65"
      className={`h-10 lg:h-11 w-auto transition-colors ${dark ? "text-white" : "text-black"}`}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Sky's the Limit"
    >
      <path d="M67.135 0.128519C67.652 -0.196006 68.2334 0.128885 68.1815 0.713323L67.3968 9.54021C67.3449 10.1246 66.6985 10.5303 66.2334 10.2704L65.1216 9.64909L47.85 37.1802L40.7634 38.3062L47.3545 27.7735L44.5262 10.3075L51.989 9.12182L53.1384 18.5307L60.361 6.98876L59.2088 6.34488C58.7437 6.08496 58.8087 5.35441 59.3257 5.02989L67.135 0.128519Z"/>
      <path d="M71.1165 33.4834C65.8486 34.3204 63.2147 32.2537 64.1554 27.9623L64.8452 24.794L70.8657 23.8374L70.364 26.2112C70.1131 27.2706 70.7402 27.9356 72.0572 27.7264C73.3742 27.5171 74.1268 26.824 74.3776 25.6372C74.833 23.5405 73.6867 22.8532 71.1733 21.3462C70.9177 21.193 70.6476 21.031 70.364 20.8584C67.6046 19.194 66.852 16.3186 67.5419 13.1503C68.3571 9.38855 72.0572 5.23219 77.6387 4.34539C82.154 3.62799 84.6626 6.2244 83.8473 9.98614L83.2202 12.7621L77.137 13.7286L77.6387 11.4186C77.8268 10.4329 77.2624 9.94907 76.3217 10.0985C75.4437 10.238 74.3776 10.9172 74.1268 12.0403C73.5142 14.7517 74.2777 15.1168 77.2939 16.5589L77.5133 16.6638C80.3354 17.9997 81.715 21.2215 80.8998 24.6647C80.1472 28.5439 76.4471 32.6365 71.1165 33.4834Z"/>
      <path d="M42.6448 10.606L35.4328 11.7519L29.0988 23.5911L31.6073 12.3597L24.458 13.4955L18.0373 41.9168L25.1866 40.781L26.7784 33.7094L28.7225 30.3418L29.7707 40.0557L37.2963 38.86L34.8684 24.14L42.6448 10.606Z"/>
      <path d="M7.14903 43.6466C1.88112 44.4836 -0.752833 42.4169 0.187864 38.1254L0.877709 34.9571L6.89817 34.0006L6.39647 36.3743C6.14561 37.4338 6.77275 38.0988 8.08972 37.8895C9.4067 37.6803 10.1593 36.9872 10.4101 35.8004C10.8655 33.7037 9.71922 33.0164 7.2058 31.5094C6.95009 31.356 6.68023 31.1942 6.39647 31.0216C3.63709 29.3572 2.88453 26.4818 3.57438 23.3135C4.45236 19.6055 8.08972 15.4591 13.7339 14.5623C18.2493 13.8449 20.7578 16.4413 19.9425 20.2031L19.3154 22.9791L13.2322 23.9456L13.7339 21.6356C13.922 20.6498 13.3576 20.166 12.4169 20.3155C11.4762 20.4649 10.4728 21.1341 10.222 22.2573C9.60943 24.9687 10.3729 25.3337 13.3891 26.7758L13.6085 26.8808C16.4306 28.2166 17.8103 31.4385 16.995 34.8816C16.1797 38.7071 12.4796 42.7997 7.14903 43.6466Z"/>
      <path d="M9.74854 47.8689L9.07554 51.0305L6.47487 51.444L3.82411 63.8952L0.0206591 64.5L2.67115 52.0489L3.12751e-09 52.4736L0.672993 49.3121L9.74854 47.8689Z"/>
      <path d="M13.8063 53.3329L15.1674 47.0072L11.3215 47.6188L7.96119 63.2373L11.8072 62.6258L13.0373 56.9061L15.3093 56.5448L14.0786 62.2646L17.9241 61.6531L21.2844 46.0345L17.4389 46.646L16.0778 52.9717L13.8063 53.3329Z"/>
      <path d="M31.0328 44.4844L30.376 47.5489L26.6694 48.1384L26.0125 51.2036L28.4536 50.8154L27.8171 53.7823L25.3761 54.1704L24.6811 57.4111L28.3878 56.8217L27.6851 60.1009L20.1471 61.2996L23.4948 45.683L31.0328 44.4844Z"/>
      <path d="M37.8765 55.2187L40.508 42.9777L36.6656 43.5887L33.3075 59.2069L40.8674 58.0047L41.5936 54.6277L37.8765 55.2187Z"/>
      <path d="M50.0425 41.4615L46.7089 57.0758L42.8937 57.6825L46.2274 42.0682L50.0425 41.4615Z"/>
      <path d="M57.8868 47.4734L57.8581 40.2187L52.5869 41.0569L49.2303 56.6749L52.661 56.1293L54.9267 45.5872L54.573 55.8253L57.1815 55.4105L61.5205 44.5387L59.2542 55.0809L63.096 54.47L66.4526 38.852L61.1104 39.7015L57.8868 47.4734Z"/>
      <path d="M72.7892 37.8444L69.4555 53.4587L65.6403 54.0654L68.9745 38.451L72.7892 37.8444Z"/>
      <path d="M83.327 39.2233L84 36.0617L74.9245 37.5049L74.2515 40.6665L76.9231 40.2416L74.2724 52.6928L78.0756 52.088L80.7263 39.6369L83.327 39.2233Z"/>
    </svg>
  );
}

/* Relative paths are main-site pages; absolute URLs (e.g. the blog host) pass through. */
const navHref = (href) => (href.startsWith("/") ? mainHref(href) : href);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // All pages (Home + all subpages) currently open with a dark hero, so use dark navbar until scrolled
  const darkMode = !scrolled;

  // Clean, minimal header (full nav + Login + Sign Up, no donor) for standalone landing pages.
  const minimal = ["/entrepreneur-grants", "/join-skys-the-limit-free", "/friends-family-fund"].includes(pathname);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200/70"
          : "bg-black/50 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <SiteLink href={mainHref("/")} className="group">
          <Logo dark={darkMode} />
        </SiteLink>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.href} className="relative group">
                <SiteLink
                  href={navHref(link.href)}
                  data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                    pathname === link.href || link.children.some((c) => c.href === pathname)
                      ? (darkMode ? "text-brand-orbit" : "text-black")
                      : (darkMode ? "text-white/85 hover:text-white" : "text-neutral-700 hover:text-black")
                  }`}
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                </SiteLink>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                  <div className="min-w-[220px] rounded-2xl border border-neutral-200 bg-white shadow-xl p-2" data-testid={`nav-dropdown-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    {link.children.map((child) => (
                      child.external ? (
                        <a
                          key={child.href}
                          href={child.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`nav-child-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className="block px-4 py-2.5 rounded-xl text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:text-black transition-colors"
                        >
                          {child.label}
                        </a>
                      ) : (
                        <SiteLink
                          key={child.href}
                          href={navHref(child.href)}
                          data-testid={`nav-child-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                            pathname === child.href ? "bg-neutral-100 text-black" : "text-neutral-700 hover:bg-neutral-100 hover:text-black"
                          }`}
                        >
                          {child.label}
                        </SiteLink>
                      )
                    ))}
                  </div>
                </div>
              </div>
            ) : link.external ? (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`text-sm font-medium link-underline transition-colors ${
                  darkMode ? "text-white/85 hover:text-white" : "text-neutral-700 hover:text-black"
                }`}
              >
                {link.label}
              </a>
            ) : (
              <SiteLink
                key={link.href}
                href={navHref(link.href)}
                className={`text-sm font-medium link-underline transition-colors ${
                  pathname === link.href
                    ? (darkMode ? "text-brand-orbit" : "text-black")
                    : (darkMode ? "text-white/85 hover:text-white" : "text-neutral-700 hover:text-black")
                }`}
              >
                {link.label}
              </SiteLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <a
            href="https://app.skysthelimit.org/sign-in"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login-btn"
            className={`text-sm font-medium link-underline transition-colors ${
              darkMode ? "text-white/85 hover:text-white" : "text-neutral-700 hover:text-black"
            }`}
          >
            Login
          </a>
          {minimal ? (
            <Button asChild className={`rounded-full h-11 px-6 group font-semibold transition-colors ${
              darkMode ? "hover:bg-white" : "bg-black hover:bg-neutral-800 text-white"
            }`} style={darkMode ? { backgroundColor: "#FFDA7C", color: "#000000" } : {}}>
              <a href="https://app.skysthelimit.org/sign-up" target="_blank" rel="noopener noreferrer" data-testid="nav-signup-btn">
                Sign Up
                <ArrowUpRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Button>
          ) : (
            <>
              <a
                href="https://app.skysthelimit.org/sign-up"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-signup-btn"
                className={`text-sm font-medium link-underline transition-colors ${
                  darkMode ? "text-white/85 hover:text-white" : "text-neutral-700 hover:text-black"
                }`}
              >
                Sign Up
              </a>
              <Button asChild className={`rounded-full h-11 px-6 group font-semibold transition-colors ${
                darkMode
                  ? "bg-brand-orbit text-black hover:bg-white"
                  : "bg-black hover:bg-neutral-800 text-white"
              }`} style={darkMode ? {backgroundColor: '#FFDA7C', color: '#000000'} : {}}>
                <SiteLink href={mainHref("/donate")} data-testid="nav-become-donor">
                  Become a Donor
                  <ArrowUpRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </SiteLink>
              </Button>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border ${
            darkMode ? "border-white/20 bg-white/10 text-white" : "border-neutral-200 bg-white/70 text-black"
          }`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className={`lg:hidden ${darkMode ? "bg-black border-t border-white/10" : "bg-white border-t border-neutral-200"}`}>
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <div key={link.href}>
                {link.external ? (
                  <a href={link.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-mobile-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`font-medium ${darkMode ? "text-white/90" : "text-neutral-800"}`}>
                    {link.label}
                  </a>
                ) : (
                  <SiteLink href={navHref(link.href)} className={`font-medium ${darkMode ? "text-white/90" : "text-neutral-800"}`}>
                    {link.label}
                  </SiteLink>
                )}
                {link.children && (
                  <div className="mt-3 ml-3 flex flex-col gap-3 border-l border-white/15 pl-4">
                    {link.children.filter((c) => c.href !== link.href).map((child) => (
                      child.external ? (
                        <a key={child.href} href={child.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-mobile-child-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-neutral-600"}`}>
                          {child.label}
                        </a>
                      ) : (
                        <SiteLink key={child.href} href={navHref(child.href)} data-testid={`nav-mobile-child-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-neutral-600"}`}>
                          {child.label}
                        </SiteLink>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a href="https://app.skysthelimit.org/sign-in" target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-login-btn" className={`font-medium ${darkMode ? "text-white/90" : "text-neutral-800"}`}>Login</a>
            <a href="https://app.skysthelimit.org/sign-up" target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-signup-btn" className={`font-medium ${darkMode ? "text-white/90" : "text-neutral-800"}`}>Sign Up</a>
            {!minimal && (
              <Button asChild className={`w-full rounded-full h-11 group font-semibold ${darkMode ? "bg-brand-orbit text-black" : "bg-black text-white"}`} style={darkMode ? {backgroundColor: '#FFDA7C', color: '#000000'} : {}}>
                <SiteLink href={mainHref("/donate")} data-testid="nav-mobile-become-donor">
                  Become a Donor
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </SiteLink>
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
